const DURATION_READ_TIMEOUT_MS = 4000;
const CONTAINER_METADATA_CHUNK_BYTES = 512 * 1024;
const MP4_EPOCH_MS = Date.UTC(1904, 0, 1);

export type BrowserVideoFileMetadata = {
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  videoRecordedAt: Date | null;
};

const readPositiveInteger = (value: number): number | null => Number.isInteger(value) && value > 0 ? value : null;

const isPlausibleRecordedAt = (date: Date) => {
  const timeMs = date.getTime();
  return Number.isFinite(timeMs)
    && timeMs >= Date.UTC(1990, 0, 1)
    && timeMs <= Date.now() + 60_000;
};

const findMvhdTypeIndex = (bytes: Uint8Array): number | null => {
  for (let index = 0; index <= bytes.length - 4; index++) {
    if (bytes[index] === 0x6d && bytes[index + 1] === 0x76 && bytes[index + 2] === 0x68 && bytes[index + 3] === 0x64) {
      return index;
    }
  }
  return null;
};

const readRecordedAtFromMvhdHeader = (header: Uint8Array): Date | null => {
  if (header.length < 12) return null;

  const version = header[0];
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);

  if (version === 0) {
    const secondsSinceMp4Epoch = view.getUint32(4, false);
    if (secondsSinceMp4Epoch === 0) return null;
    return new Date(MP4_EPOCH_MS + secondsSinceMp4Epoch * 1000);
  }

  if (version === 1) {
    const secondsSinceMp4Epoch = Number(view.getBigUint64(4, false));
    if (secondsSinceMp4Epoch === 0) return null;
    return new Date(MP4_EPOCH_MS + secondsSinceMp4Epoch * 1000);
  }

  return null;
};

const readContainerRecordedAt = async (file: File): Promise<Date | null> => {
  const chunkStarts = [
    Math.max(0, file.size - CONTAINER_METADATA_CHUNK_BYTES),
    0,
  ];

  for (const chunkStart of chunkStarts) {
    const chunkEnd = Math.min(file.size, chunkStart + CONTAINER_METADATA_CHUNK_BYTES);
    if (chunkEnd <= chunkStart) continue;

    const chunk = new Uint8Array(await file.slice(chunkStart, chunkEnd).arrayBuffer());
    const mvhdTypeIndex = findMvhdTypeIndex(chunk);
    if (mvhdTypeIndex == null) continue;

    const absoluteMvhdTypeIndex = chunkStart + mvhdTypeIndex;
    const header = new Uint8Array(
      await file.slice(absoluteMvhdTypeIndex + 4, absoluteMvhdTypeIndex + 16).arrayBuffer(),
    );
    const recordedAt = readRecordedAtFromMvhdHeader(header);
    if (recordedAt && isPlausibleRecordedAt(recordedAt)) return recordedAt;
  }

  return null;
};

const readFileLastModifiedRecordedAt = (file: File): Date | null => {
  if (file.lastModified <= 0) return null;
  const recordedAt = new Date(file.lastModified);
  return isPlausibleRecordedAt(recordedAt) ? recordedAt : null;
};

export const readBrowserVideoFileRecordedAt = async (file: File): Promise<Date | null> =>
  readContainerRecordedAt(file).then(recordedAt => recordedAt ?? readFileLastModifiedRecordedAt(file));

const readBrowserVideoElementMetadata = (file: File): Promise<Pick<BrowserVideoFileMetadata, 'durationSeconds' | 'width' | 'height'>> =>
  new Promise(resolve => {
    let objectUrl = '';
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      resolve({ durationSeconds: null, width: null, height: null });
      return;
    }

    const video = document.createElement('video');
    let settled = false;

    const finish = (metadata: Pick<BrowserVideoFileMetadata, 'durationSeconds' | 'width' | 'height'>) => {
      if (settled) return;
      settled = true;
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // ignore
      }
      resolve(metadata);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      finish({
        durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
        width: readPositiveInteger(video.videoWidth),
        height: readPositiveInteger(video.videoHeight),
      });
    video.onerror = () => finish({ durationSeconds: null, width: null, height: null });
    window.setTimeout(() => finish({ durationSeconds: null, width: null, height: null }), DURATION_READ_TIMEOUT_MS);
    video.src = objectUrl;
  });

/** Reads duration, dimensions, and recorded-at without playing. Resolves nulls when unavailable. */
export const readBrowserVideoFileMetadata = (file: File): Promise<BrowserVideoFileMetadata> =>
  Promise.all([
    readBrowserVideoElementMetadata(file),
    readBrowserVideoFileRecordedAt(file),
  ]).then(([elementMetadata, videoRecordedAt]) => ({
    ...elementMetadata,
    videoRecordedAt,
  }));

/** Reads duration without playing. Resolves null when the browser cannot decode the container. */
export const readBrowserVideoFileDurationSeconds = (file: File): Promise<number | null> =>
  readBrowserVideoFileMetadata(file).then(metadata => metadata.durationSeconds);
